import logger from '@goldcast/logger';
import {
  createInPersonEmailAttendance,
  internalRegistrationUser,
  createBulkRegistrationFlatfileV2,
  createValidateFlatfileUsers
} from '@goldcast/api/backend';

import { showSuccessNotification, showErrorNotification } from '@/utils/utils';
import { getEnv } from '@/utils/env';

export const ROLE = {
  ATTENDEE: 0,
  SPEAKER: 4,
  ORGANIZER: 2,
  SPONSOR: 6,
  CHECK_IN_STAFF: 8
};

const registrationErrorMessages = {
  USER_ALREADY_IN_EVENT:
    'User is already registered for the event. They will receive a new confirmation email shortly.',
  REGISTRATION_LIMIT_REACHED:
    'Your registration could not be completed. This event has reached its registration capacity.',
  TOO_MANY_REQUESTS: 'User already exists in event with Registered status. Please review in registrants list.',
  USER_ALREADY_WAITLISTED_FOR_EVENT:
    'User already exists in event with waitlisted status. Please review in registrants list.',
  USER_ALREADY_CANCELED_FOR_EVENT:
    'User already exists in event with cancelled status. Please review in registrants list.',
  USER_ALREADY_REJECTED_FOR_EVENT:
    'User already exists in event with rejected status. Please review in registrants list.',
  MAX_RE_REGISTRATION_LIMIT_REACHED:
    'User already exists in event with Registered status. Please review in registrants list.'
};

const state = {
  loadingRegistration: false,
  errorRegistration: null,
  allSelected: false,
  selectedUsers: {},
  roleIcons: {
    [ROLE.SPEAKER]: `${getEnv('VUE_APP_ASSETS_CDN')}/assets/icons/speaker_icon.svg`,
    [ROLE.ORGANIZER]: `${getEnv('VUE_APP_ASSETS_CDN')}/assets/icons/organizer_icon.svg`,
    [ROLE.SPONSOR]: `${getEnv('VUE_APP_ASSETS_CDN')}/assets/icons/sponsor_icon.svg`,
    [ROLE.CHECK_IN_STAFF]: `${getEnv('VUE_APP_ASSETS_CDN')}/assets/icons/checkin_staff_icon.svg`
  },
  roleNames: {
    [ROLE.ATTENDEE]: 'Attendee',
    [ROLE.SPEAKER]: 'Speaker',
    [ROLE.ORGANIZER]: 'Organizer',
    [ROLE.SPONSOR]: 'Sponsor',
    [ROLE.CHECK_IN_STAFF]: 'Check-in Staff'
  },
  lastSearchedQuery: null,
  lastSearchedQueryResultSize: 0,
  loadingFlatFileInstance: false,
  errorFlatFileRegistration: null,
  flatFileTokenBulkRegistration: null,
  loadingFlatFileSubmit: null
};

const mutations = {
  setLoadingRegistration(state, loadingRegistration) {
    state.loadingRegistration = loadingRegistration;
  },
  setErrorRegistration(state, errorRegistration) {
    state.errorRegistration = errorRegistration;
  },
  toggleSelectAll(state, details) {
    state.selectedUsers = {};
    state.allSelected = details.allSelected;
    if (details.allSelected) {
      const allUsers = details?.users.reduce((obj, item) => {
        obj[item.id] = item;
        return obj;
      }, {});

      state.selectedUsers = {
        ...allUsers
      };
    } else {
      state.selectedUsers = {};
    }
  },
  toggleSelection(state, user) {
    if (user) {
      state.selectedUsers = {
        ...state.selectedUsers,
        [user.id]: state.selectedUsers[user.id] ? null : user
      };
    }
  },
  setLoadingFlatFileInstance(state, loadingFlatFileInstance) {
    state.loadingFlatFileInstance = loadingFlatFileInstance;
  },
  setFlatFileTokenBulkRegistration(state, flatFileTokenBulkRegistration) {
    state.flatFileTokenBulkRegistration = flatFileTokenBulkRegistration;
  },
  setErrorFlatFileRegistration(state, errorFlatFileRegistration) {
    state.errorFlatFileRegistration = errorFlatFileRegistration;
  },
  setLoadingFlatFileSubmit(state, loadingFlatFileSubmit) {
    state.loadingFlatFileSubmit = loadingFlatFileSubmit;
  }
};

const actions = {
  async registerUser(context, userData) {
    context.commit('setErrorRegistration', null);
    context.commit('setLoadingRegistration', true);
    try {
      const registeredUser = await internalRegistrationUser({ body: userData });
      showSuccessNotification(
        'Your user will now be registered. It may take a few minutes for registration to be processed. Please click the refresh icon at the top of this page to view the updated list of registrants.',
        20000
      );
      logger.info('New User Registered', registeredUser);
    } catch (error) {
      if (registrationErrorMessages[error?.apiErrorCode]) {
        showErrorNotification(registrationErrorMessages[error.apiErrorCode]);
      } else {
        showErrorNotification('User registration failed', 10000);
        context.commit('setErrorRegistration', error);
        logger.error('error in registerUser user', error);
      }
    } finally {
      context.commit('setLoadingRegistration', false);
    }
  },
  async flatFileRegisterUser(context, { eventId, batchId }) {
    context.commit('setErrorFlatFileRegistration', null);
    context.commit('setLoadingFlatFileSubmit', true);
    try {
      await createBulkRegistrationFlatfileV2({
        id: eventId,
        body: { sheet_id: batchId }
      });
    } catch (error) {
      context.commit('setErrorFlatFileRegistration', error);
      logger.error(error);
      return error;
    } finally {
      context.commit('setLoadingFlatFileSubmit', false);
    }
  },
  async flatFileValidation(context, { validateRecord }) {
    try {
      const response = await createValidateFlatfileUsers({ body: validateRecord });
      logger.info('Flat file validation', response);
      return { response };
    } catch (error) {
      showErrorNotification('Flat file Validation Failed');
      logger.error(error);
      return { error };
    }
  },
  async setInPersonAttendance(context, { eventId, payload }) {
    return createInPersonEmailAttendance({
      id: eventId,
      body: payload
    });
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};
